// import firebase from 'firebase/compat/app'
import {initializeApp} from 'firebase/app'
import {connectFirestoreEmulator, getFirestore} from 'firebase/firestore'
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword as _createUserWithEmailAndPassword,
  getAuth,
  signInWithEmailAndPassword as _signInWithEmailAndPassword,
  sendSignInLinkToEmail as _sendSignInLinkToEmail,
  signInWithEmailLink as _signInWithEmailLink,
  sendPasswordResetEmail as _sendPasswordResetEmail,
} from 'firebase/auth'
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable as _httpsCallable,
} from 'firebase/functions'
import { connectStorageEmulator, getStorage } from 'firebase/storage'

import { initAppCheck } from './appCheck'
import config from './firebaseConfig'
import { isLocalWebDevelopmentHost } from '../platform/runtime'

const FIREBASE_EMULATOR_HOST = '127.0.0.1'
const FIREBASE_EMULATOR_PORTS = {
  auth: 9099,
  firestore: 5402,
  functions: 5501,
  storage: 9199,
}

function shouldUseFirebaseEmulators() {
  if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true') {
    return true
  }

  // #1690 (spec addendum §5.1): was `window.location.hostname === 'localhost'`
  // directly — wrong inside a native WebView, whose default hostname IS
  // "localhost" on a real production launch. isLocalWebDevelopmentHost()
  // excludes native first.
  return isLocalWebDevelopmentHost()
}

export const app = initializeApp(config)
export default app

export const auth = getAuth(app)

export const createUserWithEmailAndPassword = _createUserWithEmailAndPassword.bind(null, auth)
export const signInWithEmailAndPassword = _signInWithEmailAndPassword.bind(null, auth)
export const sendSignInLinkToEmail = _sendSignInLinkToEmail.bind(null, auth)
export const signInWithEmailLink = _signInWithEmailLink.bind(null, auth)
export const sendPasswordResetEmail = _sendPasswordResetEmail.bind(null, auth)

export const firestore = getFirestore(app)
// #2075 (Lion's decision, 19.9.2026): every Cloud Function now defaults to
// europe-west3 (see functions/app.js's setGlobalOptions) — co-located with
// Firestore (`kravcalc`), which has always run there; #958 below was the
// first, narrow instance of that same fix, applied to one callable years
// before the rest followed.
export const functions = getFunctions(app, 'europe-west3')
// #958's reason for a second Functions instance — a callable pinned to a
// non-default region needs its own SDK object, since the client bakes the
// region into the instance at creation — no longer applies once the
// DEFAULT region is europe-west3 too. `functionsEuropeWest3` is kept as an
// alias of `functions` (not a second instance) purely so the existing
// `httpsCallableEuropeWest3(...)` call sites (src/pages/Attendance/*,
// src/services/stations.js) do not need to change in this PR.
// ⚠️ Old/cached clients (a stale PWA, a browser tab open across the deploy)
// keep calling whatever region THEIR loaded bundle points at — this file
// changing is not itself the cutover; see #2075 for the staged rollout that
// has to keep the us-central1 deployments alive until traffic there is ~0.
export const functionsEuropeWest3 = functions
export const storage = getStorage(app)

if (shouldUseFirebaseEmulators()) {
  connectAuthEmulator(auth, `http://${FIREBASE_EMULATOR_HOST}:${FIREBASE_EMULATOR_PORTS.auth}`, {
    disableWarnings: true,
  })
  connectFirestoreEmulator(
    firestore,
    FIREBASE_EMULATOR_HOST,
    FIREBASE_EMULATOR_PORTS.firestore
  )
  // #2075: `functionsEuropeWest3` is now the SAME instance as `functions`
  // (see above), so a second connectFunctionsEmulator() call here would
  // target an instance already pointed at the emulator — the Firebase JS
  // SDK expects this call once per instance, immediately after getFunctions().
  connectFunctionsEmulator(
    functions,
    FIREBASE_EMULATOR_HOST,
    FIREBASE_EMULATOR_PORTS.functions
  )
  connectStorageEmulator(
    storage,
    FIREBASE_EMULATOR_HOST,
    FIREBASE_EMULATOR_PORTS.storage
  )
}

// #194: App Check in monitoring mode. Started after the emulator wiring so a
// local/emulator run never reaches reCAPTCHA. Nothing is enforced server-side —
// this only produces the attested-vs-unattested numbers we need before deciding
// whether to enforce.
export const appCheck = initAppCheck(app, { enabled: !shouldUseFirebaseEmulators() })

export const httpsCallable = _httpsCallable.bind(null, functions)
// #2075: kept as a distinct export, bound to the same instance as
// httpsCallable above — see functionsEuropeWest3's own comment.
export const httpsCallableEuropeWest3 = _httpsCallable.bind(null, functionsEuropeWest3)